import type { AxiosInstance } from 'axios';
import { BookingState } from 'constants/bookings';
import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';
import { useCallback, useMemo, useState } from 'react';
import { dateFormatForAPI, formatDate } from 'utils/date';

export type StatementState =
  | 'void'
  | 'draft'
  | 'billed'
  | 'part_paid'
  | 'fully_paid';

import type { PaymentState } from 'types/enums';

export type { PaymentState };

interface Space {
  id: number;
  name?: string;
}

export interface BookingItem {
  space: Space;
  id: number;
  // Present on the basket (tentative) payload, which groups items by date;
  // absent from the per-space booking-items response.
  date?: string;
  state: BookingState;
  startTime: string;
  endTime: string;
  totalCost: string;
  cancellable: boolean;
  cancellableErrors: string[];
  statementId: number;
  statementNumber: string;
  statementDueDate: string;
  statementState: StatementState;
  paymentState: PaymentState;
  recurring: boolean;
  // Present when a discount applies to the item; the booking card groups by
  // discountId to decide whether to show one figure for the whole booking.
  discount?: number;
  discountId?: number;
}

// The per-space response omits `space` (it is implied by the URL), so it is
// filled in from the id that was requested.
const fixBookingItem =
  (spaceId: number) =>
  (item: BookingItem | Omit<BookingItem, 'space'>): BookingItem =>
    'space' in item ? item : { ...item, space: { id: spaceId } };

const getBookingItems =
  (apiRequest: AxiosInstance, dates: string[]) => async (spaceId: number) => {
    const {
      data: { bookingItems = [] },
    } = (await apiRequest.get(`/v1/spaces/${spaceId}/booking_items`, {
      params: { dates },
    })) as unknown as { data: { bookingItems: BookingItem[] } };

    return bookingItems.map(fixBookingItem(spaceId));
  };

const useBookingItems = (
  dates: Date | string | Array<Date | string> = [],
  spaceIds: number[] = []
) => {
  const [bookingItems, setBookingItems] = useState<BookingItem[]>([]);

  const apiRequest = useApiRequest();
  const requestValid = !!(
    dates &&
    (!Array.isArray(dates) || dates.length > 0) &&
    spaceIds.length > 0
  );

  const datesJson = useMemo(
    () =>
      JSON.stringify(
        Array.isArray(dates)
          ? dates.map(date => formatDate(date, dateFormatForAPI))
          : [formatDate(dates, dateFormatForAPI)]
      ),
    [dates]
  );
  const spaceIdsJson = useMemo(() => JSON.stringify(spaceIds), [spaceIds]);

  const clearData = useCallback(async () => {
    setBookingItems([]);
  }, []);

  const fetchData = useCallback(async () => {
    const itemLists = await Promise.all(
      JSON.parse(spaceIdsJson).map(
        getBookingItems(apiRequest, JSON.parse(datesJson))
      )
    );

    setBookingItems(itemLists.flat());
  }, [apiRequest, datesJson, spaceIdsJson]);

  const { loading, error, refresh } = useAsync(
    requestValid ? fetchData : clearData,
    requestValid
  );

  return { data: bookingItems, loading, error, refresh };
};

export default useBookingItems;
